import React, { ReactNode, useState } from 'react';
import { CarFront, Menu, X, User as UserIcon, LogOut, Coins, Plus, AlertCircle } from 'lucide-react';
import { User } from '../types';
import { resendVerificationEmail } from '../services/authService';

interface LayoutProps {
  children: ReactNode;
  user: User | null;
  onLogout: () => void;
  onNavigate: (page: string) => void;
  currentPage: string;
}

const Layout: React.FC<LayoutProps> = ({ children, user, onLogout, onNavigate, currentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleResendVerification = async () => {
    setSendingVerification(true);
    try {
      await resendVerificationEmail();
      setVerificationSent(true);
      setTimeout(() => setVerificationSent(false), 5000);
    } catch (error) {
      console.error('Error sending verification email:', error);
    } finally {
      setSendingVerification(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex flex-col font-sans">
      {/* Email Verification Banner */}
      {user && user.emailVerified === false && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-yellow-400 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>Verifique seu e-mail para acessar todas as funcionalidades.</span>
            </div>
            <button
              onClick={handleResendVerification}
              disabled={sendingVerification || verificationSent}
              className="text-xs text-yellow-400 hover:text-yellow-300 underline disabled:no-underline disabled:opacity-50"
            >
              {verificationSent ? 'E-mail enviado!' : sendingVerification ? 'Enviando...' : 'Reenviar e-mail'}
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            {/* Logo */}
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => onNavigate('home')}
            >
              <div className="bg-emerald-500/10 p-2 rounded-lg border border-emerald-500/20">
                <CarFront className="w-6 h-6 text-emerald-400" />
              </div>
              <span className="font-bold text-xl tracking-tight text-white">
                Pedigree do <span className="text-emerald-400">Carro</span>
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              {user ? (
                <>
                  <button 
                    onClick={() => onNavigate('dashboard')}
                    className={`text-sm font-medium hover:text-white transition-colors ${currentPage === 'dashboard' ? 'text-white' : 'text-slate-400'}`}
                  >
                    Dashboard
                  </button>
                  <div className="h-6 w-px bg-slate-800"></div>
                  <div className="flex items-center gap-1">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-l-full border border-slate-700 border-r-0">
                      <Coins className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm font-bold text-white">{user.credits} Créditos</span>
                    </div>
                    <button
                      onClick={() => onNavigate('buy-credits')}
                      className="flex items-center justify-center w-8 h-8 bg-emerald-600 hover:bg-emerald-500 rounded-r-full border border-emerald-600 hover:border-emerald-500 transition-colors"
                      title="Comprar créditos"
                    >
                      <Plus className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 pl-4">
                    {user.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt={user.name}
                        className="w-8 h-8 rounded-full border border-slate-700"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                        <UserIcon className="w-4 h-4 text-slate-400" />
                      </div>
                    )}
                    <div className="text-right">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-slate-400">{user.email}</p>
                    </div>
                    <button 
                      onClick={onLogout}
                      className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-red-400"
                      title="Sair"
                    >
                      <LogOut className="w-5 h-5" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => onNavigate('home')}
                    className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
                  >
                    Funcionalidades
                  </button>
                  <button 
                    onClick={() => onNavigate('login')}
                    className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-all shadow-lg shadow-emerald-500/20"
                  >
                    Entrar / Cadastrar
                  </button>
                </>
              )}
            </div>

            {/* Mobile Button */}
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 text-slate-400 hover:text-white">
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-900 border-b border-slate-800 p-4 space-y-4">
            {user ? (
              <>
                 <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      {user.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt={user.name}
                          className="w-10 h-10 rounded-full border border-slate-700"
                        />
                      ) : (
                        <div className="bg-slate-700 p-2 rounded-full">
                          <UserIcon className="w-5 h-5 text-slate-300" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-bold text-white">{user.name}</p>
                        <div className="flex items-center gap-1.5 text-xs text-yellow-400">
                           <Coins className="w-3 h-3" />
                           <span>{user.credits} créditos</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => { onNavigate('buy-credits'); setIsMenuOpen(false); }}
                      className="flex items-center justify-center w-8 h-8 bg-emerald-600 hover:bg-emerald-500 rounded-full transition-colors"
                      title="Comprar créditos"
                    >
                      <Plus className="w-4 h-4 text-white" />
                    </button>
                 </div>
                 <button onClick={() => { onNavigate('dashboard'); setIsMenuOpen(false); }} className="block w-full text-left py-2 text-slate-300">Dashboard</button>
                 <button onClick={() => { onNavigate('buy-credits'); setIsMenuOpen(false); }} className="block w-full text-left py-2 text-emerald-400">Comprar Créditos</button>
                 <button onClick={onLogout} className="block w-full text-left py-2 text-red-400">Sair</button>
              </>
            ) : (
              <>
                <button onClick={() => { onNavigate('home'); setIsMenuOpen(false); }} className="block w-full text-left py-2 text-slate-300">Home</button>
                <button onClick={() => { onNavigate('login'); setIsMenuOpen(false); }} className="block w-full py-3 bg-emerald-600 text-center rounded-lg text-white font-medium">Entrar</button>
              </>
            )}
          </div>
        )}
      </nav>

      <main className="flex-1 w-full">
        {children}
      </main>

      <footer className="bg-slate-950 border-t border-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© 2024 Pedigree do Carro. Todos os direitos reservados.</p>
          <p className="mt-2 text-xs">Dados fornecidos via integração com APIs oficiais</p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
